/**
 * 답변을 그릴 만큼만 읽는 마크다운.
 *
 * 라이브러리를 들이지 않는다 — 답변에 나오는 것은 제목·문단·목록·표·코드블록뿐이고,
 * 표를 읽을 수 있어야 엑셀 버튼을 켤지 정할 수 있다. 그 이상은 하지 않는다.
 */

export type Block =
  | { type: 'heading' | 'paragraph' | 'list' | 'code'; text: string }
  | { type: 'table'; rows: string[][] };

const HEADING = /^#{1,3}\s+(.*)$/;
const BULLET = /^[-*]\s+/;
// | --- | :--: | 처럼 생긴 표의 구분선.
const DIVIDER = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/;

function cells(line: string): string[] {
  return line.replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());
}

export function blocks(source: string): Block[] {
  const lines = source.split(/\r?\n/);
  const result: Block[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line.trim()) continue;

    if (line.startsWith('```')) {
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith('```')) {
        code.push(lines[i]);
        i += 1;
      }
      result.push({ type: 'code', text: code.join('\n') });
      continue;
    }

    // 표는 머리줄 다음이 구분선일 때만 표다. 값에 세로줄이 들어간 문장을 표로 읽으면
    // 엑셀 버튼이 켜지고 빈 시트가 나온다.
    if (line.includes('|') && DIVIDER.test(lines[i + 1] || '')) {
      const rows = [cells(line)];
      i += 2;
      while (lines[i] && lines[i].includes('|')) {
        rows.push(cells(lines[i]));
        i += 1;
      }
      i -= 1;
      result.push({ type: 'table', rows });
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      result.push({ type: 'heading', text: heading[1] });
    } else if (BULLET.test(line)) {
      result.push({ type: 'list', text: line.replace(BULLET, '') });
    } else {
      result.push({ type: 'paragraph', text: line });
    }
  }

  return result;
}

// 답 끝에 붙는 근거·알림·빠진 항목 안내. 사람은 본문만 읽고 이것은 펼쳐 본다(2026-10-22).
// 「화면 자료와 맞지 않을 수 있습니다」 경고는 여기 없다 — 꼭 봐야 하는 말이다.
const FOOTER = [
  /^조회한 화면:/,
  /^조회 중 알림:/,
  /^일부 항목은 이 자료에 값이 없어 계산에서 빠졌습니다\.?$/,
];

/**
 * 답 끝에 이어 붙은 근거 문단을 본문에서 뗀다. 글 자체는 그대로 둔다 — 다음 질문이 앞 답의
 * 「조회한 화면:」 줄에서 기간과 화면을 이어받는다(런타임).
 */
export function splitFooter(source: string): { body: string; footer: string[] } {
  const paragraphs = source.split(/\n\s*\n/);
  const footer: string[] = [];
  while (paragraphs.length > 1) {
    const last = paragraphs[paragraphs.length - 1].trim();
    if (!FOOTER.some((pattern) => pattern.test(last))) break;
    footer.unshift(last);
    paragraphs.pop();
  }
  return footer.length ? { body: paragraphs.join('\n\n').trimEnd(), footer } : { body: source, footer };
}

export function hasTable(source: string): boolean {
  return blocks(source).some((block) => block.type === 'table');
}

/** `**굵게**` 만 본다. 나머지 강조는 글자 그대로 둔다. */
export function segments(value: string): { bold: boolean; text: string }[] {
  return value.split(/(\*\*[^*]+\*\*)/g)
    .filter((part) => part !== '')
    .map((part) => (part.startsWith('**') && part.endsWith('**')
      ? { bold: true, text: part.slice(2, -2) }
      : { bold: false, text: part }));
}
